/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.
*/

import {
  LangSelector,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import talerLogo from "../assets/taler-logo-light.svg";

export type DemoNavigationItem = {
  name: "Introduction" | "Bank" | "Essay Shop" | "Donations";
  url: string;
};

export function buildDemoNavigation(
  configuredSites: Record<string, string>,
): DemoNavigationItem[] {
  const sites = new Map(
    Object.entries(configuredSites).map(([name, url]) => [
      name.toLowerCase(),
      url,
    ]),
  );
  const find = (...names: string[]): string => {
    for (const name of names) {
      const url = sites.get(name);
      if (url) return url;
    }
    return "#";
  };
  return [
    { name: "Introduction", url: find("introduction", "landing") },
    { name: "Bank", url: find("bank") },
    { name: "Essay Shop", url: find("essay shop", "blog", "merchant") },
    { name: "Donations", url: find("donations") },
  ];
}

function SiteLabel({ name }: { name: DemoNavigationItem["name"] }): VNode {
  const { i18n } = useTranslationContext();
  switch (name) {
    case "Introduction":
      return <i18n.Translate>Introduction</i18n.Translate>;
    case "Bank":
      return <i18n.Translate>Bank</i18n.Translate>;
    case "Essay Shop":
      return <i18n.Translate>Essay Shop</i18n.Translate>;
    case "Donations":
      return <i18n.Translate>Donations</i18n.Translate>;
  }
}

export function DemoBanner({
  sites,
}: {
  sites: Record<string, string>;
}): VNode {
  const { i18n } = useTranslationContext();
  const navigation = buildDemoNavigation(sites);
  return (
    <Fragment>
      <header class="border-t-[0.2rem] border-brand bg-brandMuted text-onBackground">
        <div class="mx-auto flex min-h-[6.25rem] w-[calc(100%-2rem)] max-w-[68rem] items-center py-4 max-md:min-h-[5.4rem] max-[544px]:min-h-[4.8rem]">
          <div class="flex min-w-0 items-center gap-6 max-[544px]:gap-4">
            <a
              class="inline-flex flex-none items-center"
              href="https://taler.net/"
              data-name="GNU Taler"
            >
              <img
                class="h-auto w-[clamp(5.25rem,9vw,6.25rem)] max-[544px]:w-[4.75rem]"
                src={talerLogo}
                alt={i18n.str`GNU Taler logo`}
              />
            </a>
            <div class="min-w-0">
              <h1 class="m-0 text-[clamp(1.55rem,3vw,2rem)] font-[680] leading-[1.2] tracking-tight max-[544px]:text-[1.4rem]">
                <a class="text-inherit no-underline" href="#/">
                  <i18n.Translate>Bank</i18n.Translate>
                </a>
              </h1>
              <p class="mt-1 mb-0 max-w-3xl text-[0.95rem] text-secondary max-md:text-[0.88rem] max-[544px]:hidden">
                <i18n.Translate>
                  Withdraw toy currency into your GNU Taler wallet.
                </i18n.Translate>
              </p>
            </div>
          </div>
        </div>
      </header>
      <div class="sticky top-0 z-30 border-b border-outlineVariant bg-brandMuted text-onBackground">
        <div class="mx-auto flex min-h-[3.15rem] w-[calc(100%-2rem)] max-w-[68rem] items-stretch justify-between gap-4 max-md:items-start max-md:gap-1 max-md:py-[0.15rem]">
          <nav
            class="flex items-stretch gap-[1.4rem] max-md:grid max-md:min-w-0 max-md:flex-1 max-md:grid-cols-2 max-md:gap-x-4 max-md:gap-y-0"
            aria-label={i18n.str`Demo pages`}
          >
            {navigation.map(({ name, url }) => {
              const active = name.toLowerCase() === "bank";
              return (
                <a
                  key={name}
                  href={url}
                  aria-current={active ? "page" : undefined}
                  class={`inline-flex min-h-11 items-center justify-center border-b-[0.15rem] px-0 font-semibold text-onBackground no-underline hover:text-brand max-md:min-h-9 max-md:justify-start max-md:text-[0.9rem] ${
                    active ? "border-brand text-brand" : "border-transparent"
                  }`}
                >
                  <SiteLabel name={name} />
                </a>
              );
            })}
          </nav>
          <LangSelector type="plain" />
        </div>
      </div>
    </Fragment>
  );
}
